import type { TranslationKey } from "@privacy/translations";
import { Translate, useTranslation } from "@privacy/translations";
import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { hasErrorCode } from "../api/errors";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog";
import { EditIcon, LockIcon, TrashCanIcon } from "../shared/icons";
import { SecondaryLink } from "../shared/SecondaryLink";
import { OptionCategoryFormDialog } from "./OptionCategoryFormDialog";
import type { OptionCategoryFormValues } from "../api/optionCategories";
import type { ConnectedEntityFormType, OptionCategoryListItem, OptionCategoryListTextKeys } from "./types";

type OptionCategoryRowProps = {
  item: OptionCategoryListItem;
  canUpdate: boolean;
  canDelete: boolean;
  textKeys: OptionCategoryListTextKeys;
  hasDescription: boolean;
  onUpdate: (id: string, values: OptionCategoryFormValues) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  usageLinkTo?: (formType: ConnectedEntityFormType, optionValue: string) => string;
};

export const OptionCategoryRow = ({
  item,
  canUpdate,
  canDelete,
  textKeys,
  hasDescription,
  onUpdate,
  onDelete,
  usageLinkTo,
}: OptionCategoryRowProps) => {
  const { t } = useTranslation();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteMutation = useMutation({ mutationFn: () => onDelete(item.id) });
  const isDeleteBlockedByUsage = hasErrorCode(deleteMutation.error, "OPTION_IS_IN_USE");
  const hasVisibleUsage = item.usage.length > 0;
  const inUseTooltipKey = hasVisibleUsage ? textKeys.inUseTooltip : textKeys.inUseWithoutAccessTooltip;
  const editLabel = t("OptionCategoryList.EditButton", { name: item.label });
  const deleteLabel = t("OptionCategoryList.DeleteButton", { name: item.label });

  return (
    <Stack
      direction="row"
      sx={{
        "gap": { xs: 2, md: 5 },
        "alignItems": "flex-start",
        "py": 1.75,
        "pl": 1,
        "pr": { xs: 2, sm: 5 },
        "borderBottom": theme => `1px solid ${theme.palette.divider}`,
        "&:hover": { bgcolor: theme => theme.palette.action.hover },
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} sx={{ flex: 1, minWidth: 0, gap: { xs: 1, md: 5 } }}>
        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14) }}>
            {item.label}
          </Typography>
          {item.description && (
            <Typography
              sx={{
                maxWidth: "72ch",
                fontSize: theme => theme.typography.pxToRem(13),
                color: theme => theme.palette.text.secondary,
                whiteSpace: "pre-wrap",
              }}
            >
              {item.description}
            </Typography>
          )}
        </Stack>

        {usageLinkTo && hasVisibleUsage && (
          <Stack
            sx={{
              flexShrink: 0,
              gap: 0.5,
              pt: { md: 0.25 },
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            {item.usage.map(({ formType, count }) => (
              <SecondaryLink key={formType} to={usageLinkTo(formType, item.value)}>
                {t(USAGE_TEXT_KEYS[formType], { count })}
              </SecondaryLink>
            ))}
          </Stack>
        )}
      </Stack>

      <Stack
        direction="row"
        sx={{
          width: ACTIONS_COLUMN_WIDTH,
          gap: 0.5,
          height: NAME_LINE_HEIGHT,
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "flex-end",
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {!item.canBeEdited ? (
          <Tooltip title={t(item.isShared ? "OptionCategoryList.SharedTooltip" : textKeys.lockedTooltip)}>
            <Box component="span" sx={{ display: "flex", p: 0.75 }}>
              <LockIcon size={14} />
            </Box>
          </Tooltip>
        ) : (
          <>
            {canUpdate && (
              <Tooltip title={editLabel}>
                <IconButton size="small" aria-label={editLabel} onClick={() => setIsEditDialogOpen(true)}>
                  <EditIcon size={14} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete &&
              (item.isInUse ? (
                <Tooltip title={t(inUseTooltipKey)}>
                  <Box component="span">
                    <IconButton size="small" aria-label={deleteLabel} disabled>
                      <TrashCanIcon size={14} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={deleteLabel}>
                  <IconButton size="small" aria-label={deleteLabel} onClick={() => setIsDeleteDialogOpen(true)}>
                    <TrashCanIcon size={14} />
                  </IconButton>
                </Tooltip>
              ))}
          </>
        )}
      </Stack>

      {isEditDialogOpen && (
        <OptionCategoryFormDialog
          mode="edit"
          textKeys={textKeys}
          hasDescription={hasDescription}
          defaultValues={{ label: item.label, description: item.description ?? "" }}
          onSubmit={values => onUpdate(item.id, values)}
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
      {isDeleteDialogOpen && (
        <ConfirmDeleteDialog
          isOpen={true}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            deleteMutation.reset();
          }}
          headerText={t(textKeys.deleteHeader)}
          message={
            <Translate
              translationKey="OptionCategoryList.DeleteMessage"
              values={{
                name: (
                  <Box component="span" key={item.id} sx={{ fontWeight: "bold" }}>
                    {item.label}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t(textKeys.deleteButton)}
          errorMessage={isDeleteBlockedByUsage ? t(textKeys.inUseTooltip) : undefined}
          onDelete={() => deleteMutation.mutateAsync()}
        />
      )}
    </Stack>
  );
};

const ACTIONS_COLUMN_WIDTH = 64;
const NAME_LINE_HEIGHT = 21;

const USAGE_TEXT_KEYS: Record<ConnectedEntityFormType, TranslationKey> = {
  REGISTRATIONS: "OptionCategoryList.Usage.Registrations",
  PRIVACY_PROCESSOR_REGISTRATIONS: "OptionCategoryList.Usage.ProcessorRegistrations",
  SYSTEMS: "OptionCategoryList.Usage.Systems",
};
